import React from 'react';
import { MapPin, ArrowUpRight } from 'lucide-react';
import { mockData } from '../../mock';
import { Reveal, SectionHeader, slugify } from './Shared';

const yearOf = (desc) => (desc.match(/Q\d\s+\d{4}/) || [''])[0];

const LocationCard = ({ loc, index, featured, onBook }) => {
  const slug = slugify(loc.name);
  return (
    <Reveal
      delay={index}
      className={`nl-card group relative overflow-hidden flex flex-col ${featured ? 'lg:col-span-6 min-h-[28rem]' : 'lg:col-span-4 min-h-[22rem]'}`}
    >
      <div data-testid={`nl-location-card-${slug}`} className="flex flex-col h-full">
        <div className={`relative overflow-hidden ${featured ? 'h-64' : 'h-44'}`}>
          <img
            src={loc.image}
            alt={loc.name}
            className={`nl-img-cover transition-transform duration-700 group-hover:scale-105 ${loc.comingSoon ? 'grayscale-[0.5] opacity-70' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#131720] via-transparent to-transparent" />
          <div className="absolute top-4 left-4">
            {loc.comingSoon ? (
              <span className="nl-status nl-status-soon"><span className="nl-status-dot" />Coming {yearOf(loc.description)}</span>
            ) : (
              <span className="nl-status nl-status-open"><span className="nl-status-dot" />Open now</span>
            )}
          </div>
          <div className="absolute top-4 right-4 nl-glass border border-white/10 rounded-lg px-3 py-1.5 text-xs font-bold">
            {loc.courts} Courts · {loc.comingSoon ? 'TBA' : loc.type}
          </div>
        </div>

        <div className="p-6 flex flex-col flex-1">
          <h3 className={`font-extrabold leading-tight ${featured ? 'text-2xl' : 'text-xl'}`}>{loc.name}</h3>
          <p className="flex items-start gap-2 text-slate-400 text-sm mt-2">
            <MapPin className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
            <span>{loc.address}{loc.addressLine2 ? ` · ${loc.addressLine2}` : ''}</span>
          </p>
          <p className="text-slate-400 text-sm mt-4 leading-relaxed flex-1">{loc.description}</p>
          <div className="mt-6">
            {loc.comingSoon ? (
              <a href="#franchise" data-testid={`nl-location-soon-${slug}`} className="nl-btn nl-btn-ghost nl-btn-sm w-full">
                Partner in {loc.address.split(',')[0]} <ArrowUpRight className="w-4 h-4" />
              </a>
            ) : (
              <button onClick={() => onBook(loc.name)} data-testid={`nl-book-now-${slug}`} className="nl-btn nl-btn-primary nl-btn-sm w-full">
                Book Now <ArrowUpRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
};

export const NlLocations = ({ onBook }) => {
  const open = mockData.locations.filter((l) => !l.comingSoon);
  const soon = mockData.locations.filter((l) => l.comingSoon);
  return (
    <section id="locations" data-testid="nl-locations" className="nl-section relative">
      <div className="nl-glow w-[28rem] h-[28rem] bg-amber-500/10 right-0 top-0" />
      <div className="nl-container">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeader
            label="Locations"
            title={<>Find your<br />home court.</>}
            lead="Two venues open in Kochi today, three more cities on the way. Same premium standard everywhere."
          />
          <Reveal className="flex gap-8 lg:pb-2">
            <div><div className="nl-display text-4xl font-black text-amber-500">{open.length}</div><div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-bold">Open now</div></div>
            <div><div className="nl-display text-4xl font-black">{soon.length}</div><div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-bold">Coming 2027</div></div>
            <div><div className="nl-display text-4xl font-black">{mockData.locations.reduce((a, l) => a + l.courts, 0)}</div><div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-bold">Total courts</div></div>
          </Reveal>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-5">
          {open.map((loc, i) => <LocationCard key={loc.name} loc={loc} index={i} featured onBook={onBook} />)}
          {soon.map((loc, i) => <LocationCard key={loc.name} loc={loc} index={i} onBook={onBook} />)}
        </div>
      </div>
    </section>
  );
};
